/**
 * Empresas — the companies EGBA prospects and serves.
 *
 * These are not "leads" in the marketing sense and the screen should not call
 * them that. Each row is a company with a **legal obligation** to publish: a
 * private school that owes the concluintes list (Res. CEE 44/2020, Art. 4º), a
 * corporation that owes its balance sheet (Lei 6.404/76, art. 289). The segment
 * is what says which obligation, so it is the first filter and it is shown on
 * every row — an operator working the list needs to know, at a glance, what the
 * company is legally required to do.
 *
 * Paginated by design: the imported universe is thousands of rows, and a table
 * that renders all of them freezes the browser on the first click.
 */

import { SEGMENTOS, nomeDoSegmento, segmentoPorId } from '@egba-do/shared'

import { temVertical, useAuth } from '../estado/auth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'

import { api } from '../api/cliente'
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'

interface Empresa {
  id: string
  nome: string | null
  razaoSocial: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  municipio: string | null
  uf: string | null
  segmento: string | null
  cnaePrincipal: string | null
  origem: string
  estagio: string
  score: number
  utmSource: string | null
  utmCampaign: string | null
  criadoEm: string
  /**
   * O que o Censo Escolar sabe desta empresa, quando ela é uma escola.
   *
   * Nulo tem significado: a empresa tem CNAE de educação mas o INEP não a
   * reconhece como escola em atividade — curso livre, franqueadora, holding.
   * Essas **não** têm obrigação de publicar concluintes.
   */
  inep: {
    escolaId: string
    coEntidade: number
    nome: string
    municipio: string | null
    matriculas: number
    matriculasConcluintes: number
    anoCenso: number
    origemVinculo: string | null
  } | null
}

interface Resumo {
  total: number
  porEstagio: Record<string, number>
  porOrigem: Record<string, number>
  porSegmento?: Record<string, number>
}

const ESTAGIOS = [
  { valor: 'novo', texto: 'Novo', tom: 'neutro' as const },
  { valor: 'qualificado', texto: 'Qualificada', tom: 'marca' as const },
  { valor: 'em_negociacao', texto: 'Em negociação', tom: 'atencao' as const },
  { valor: 'ganho', texto: 'Ganha', tom: 'sucesso' as const },
  { valor: 'perdido', texto: 'Perdida', tom: 'perigo' as const },
]

const ROTULO_ORIGEM: Record<string, string> = {
  importacao_cnpj: 'Base da Receita Federal',
  landing_page: 'Landing page',
  formulario: 'Formulário',
  indicacao: 'Indicação',
  manual: 'Cadastro manual',
}

function formatarCnpj(v: string | null): string {
  if (!v || v.length !== 14) return v ?? '—'
  return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`
}

function n(v: number): string {
  return new Intl.NumberFormat('pt-BR').format(v)
}

const VERTICAL_DO_SEGMENTO: Record<string, 'escolas' | 'companhias'> = {
  escola_particular: 'escolas',
  empresa_balanco: 'companhias',
}

export function Empresas() {
  const qc = useQueryClient()
  const { usuario } = useAuth()
  // O recorte de vertical decide quais segmentos esta pessoa trabalha. Quem só
  // vê companhias abre a tela já filtrada nelas — e não vê o chip de escolas.
  const segmentosPermitidos = SEGMENTOS.filter((s) =>
    temVertical(usuario, VERTICAL_DO_SEGMENTO[s.id] ?? 'escolas'),
  )
  const recorteUnico = segmentosPermitidos.length === 1 ? segmentosPermitidos[0]!.id : ''
  const [segmento, setSegmento] = useState(recorteUnico)
  const [estagio, setEstagio] = useState('')
  const [busca, setBusca] = useState('')
  /** Cruzamento com o Censo: '' todas · 'sim' escolas · 'nao' o resto. */
  const [inep, setInep] = useState('')
  const [pagina, setPagina] = useState(1)

  const consulta = new URLSearchParams({ pagina: String(pagina), porPagina: '50' })
  if (segmento) consulta.set('segmento', segmento)
  if (estagio) consulta.set('estagio', estagio)
  if (busca.trim()) consulta.set('busca', busca.trim())
  if (inep) consulta.set('inep', inep)

  const { data, isLoading } = useQuery({
    queryKey: ['empresas', segmento, estagio, busca, pagina, inep],
    queryFn: () =>
      api.get<{ dados: Empresa[]; total: number; pagina: number; porPagina: number }>(
        `/api/v1/leads?${consulta.toString()}`,
      ),
  })

  const { data: resumo } = useQuery({
    queryKey: ['empresas-resumo'],
    queryFn: () => api.get<{ dados: Resumo }>('/api/v1/leads/resumo'),
  })

  const mudarEstagio = useMutation({
    mutationFn: ({ id, novo }: { id: string; novo: string }) =>
      api.put(`/api/v1/leads/${id}/estagio`, { estagio: novo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['empresas'] })
      void qc.invalidateQueries({ queryKey: ['empresas-resumo'] })
    },
  })

  const seg = segmentoPorId(segmento)
  const porSegmento = resumo?.dados.porSegmento ?? {}
  const total = data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / 50))

  return (
    <div className="space-y-6">
      {/* --------------------------------------------------------- contadores */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="cartao p-5">
          <p className="text-sm text-slate-500">Empresas na base</p>
          <p className="mt-1 text-3xl font-semibold text-slate-900 tabular">
            {n(resumo?.dados.total ?? 0)}
          </p>
        </div>
        {segmentosPermitidos.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setSegmento(segmento === s.id ? '' : s.id)
              setPagina(1)
            }}
            className={`cartao p-5 text-left transition ${
              segmento === s.id ? 'ring-2 ring-marca-500' : 'hover:border-marca-300'
            }`}
          >
            <p className="text-sm text-slate-500">{s.nome}</p>
            <p className="mt-1 text-3xl font-semibold text-slate-900 tabular">
              {n(porSegmento[s.id] ?? 0)}
            </p>
            <p className="mt-1 text-xs text-slate-400">{s.descricao}</p>
          </button>
        ))}
      </div>

      {seg && (
        <Aviso tom="info" titulo={`Segmento: ${seg.nome}`}>
          {seg.obrigacao}
        </Aviso>
      )}

      {/* ------------------------------------------------------------- filtros */}
      <Cartao
        titulo="Empresas"
        descricao="A base importada da Receita. Quem tem obrigação de publicar concluintes é quem o Censo Escolar reconhece como escola — a coluna e o filtro do INEP dizem isso."
      >
        <div className="mb-5 grid gap-4 sm:grid-cols-3">
          <Selecao
            rotulo="Segmento"
            value={segmento}
            onChange={(e) => {
              setSegmento(e.target.value)
              setPagina(1)
            }}
            opcoes={[
              { valor: '', texto: 'Todos os segmentos' },
              ...segmentosPermitidos.map((s) => ({ valor: s.id, texto: s.nome })),
            ]}
          />
          <Selecao
            rotulo="Situação"
            value={estagio}
            onChange={(e) => {
              setEstagio(e.target.value)
              setPagina(1)
            }}
            opcoes={[
              { valor: '', texto: 'Todas' },
              ...ESTAGIOS.map((e) => ({ valor: e.valor, texto: e.texto })),
            ]}
          />
          <Selecao
            rotulo="Censo Escolar"
            value={inep}
            onChange={(e) => {
              setInep(e.target.value)
              setPagina(1)
            }}
            opcoes={[
              { valor: '', texto: 'Todas' },
              { valor: 'sim', texto: 'É escola no INEP' },
              { valor: 'nao', texto: 'Não está no INEP' },
            ]}
            ajuda="Só quem o INEP reconhece como escola em atividade tem obrigação de publicar concluintes."
          />
          <Campo
            rotulo="Buscar"
            placeholder="Razão social, e-mail, município…"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value)
              setPagina(1)
            }}
          />
        </div>

        {isLoading ? (
          <Carregando />
        ) : data?.dados.length ? (
          <>
            <Tabela>
              <Cabecalho
                colunas={['Empresa', 'Censo Escolar (INEP)', 'Local', 'Contato', 'Situação', 'Origem', 'Entrou em']}
              />
              <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
                {data.dados.map((e) => {
                  const atual = ESTAGIOS.find((x) => x.valor === e.estagio)
                  return (
                    <tr key={e.id} className="hover:bg-slate-50/70">
                      <td className="max-w-64 px-4 py-3">
                        <Link
                          to={`/empresas/${e.id}`}
                          className="block truncate font-medium text-slate-900 hover:text-marca-700 hover:underline"
                        >
                          {e.razaoSocial ?? e.nome ?? '—'}
                        </Link>
                        <p className="text-xs text-slate-500 tabular">{formatarCnpj(e.cnpj)}</p>
                      </td>
                      {/* O cruzamento com o INEP, e não o segmento. O segmento
                          vem do CNAE, que a própria empresa escolhe ao abrir; o
                          Censo é quem de fato reconhece uma escola em atividade
                          — e é isso que dispara a obrigação da Res. 44/2020. */}
                      <td className="max-w-72 px-4 py-3">
                        {e.inep ? (
                          <>
                            <Link
                              to={`/mercado/escola/${e.inep.escolaId}`}
                              className="block truncate text-sm font-medium text-marca-700 underline decoration-dotted underline-offset-2 hover:text-marca-900"
                            >
                              {e.inep.nome}
                            </Link>
                            <p className="text-xs text-slate-500">
                              INEP {e.inep.coEntidade} ·{' '}
                              {e.inep.matriculas.toLocaleString('pt-BR')} alunos
                              {e.inep.matriculasConcluintes > 0 ? (
                                <>
                                  {' · '}
                                  <strong className="text-emerald-700">
                                    {e.inep.matriculasConcluintes.toLocaleString('pt-BR')}{' '}
                                    concluintes
                                  </strong>
                                </>
                              ) : (
                                ' · sem etapa concluinte'
                              )}
                            </p>
                            {e.inep.origemVinculo && e.inep.origemVinculo !== 'declarado' && (
                              <p className="text-[11px] text-amber-700">
                                vínculo por semelhança de nome — confira
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-slate-400">
                            não é escola no Censo
                            <span className="block text-xs text-slate-400">
                              {nomeDoSegmento(e.segmento)}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {e.municipio ? `${e.municipio}/${e.uf}` : '—'}
                      </td>
                      <td className="max-w-56 px-4 py-3">
                        <p className="truncate text-sm text-slate-600">{e.email ?? '—'}</p>
                        {e.telefone && <p className="text-xs text-slate-400">{e.telefone}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={e.estagio}
                          disabled={mudarEstagio.isPending}
                          onChange={(ev) =>
                            mudarEstagio.mutate({ id: e.id, novo: ev.target.value })
                          }
                          className="rounded-lg border border-[color:var(--color-borda)] bg-white px-2 py-1 text-xs"
                          aria-label={`Situação de ${e.razaoSocial ?? e.nome ?? 'empresa'}`}
                        >
                          {ESTAGIOS.map((x) => (
                            <option key={x.valor} value={x.valor}>
                              {x.texto}
                            </option>
                          ))}
                        </select>
                        {atual && <span className="sr-only">{atual.texto}</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {ROTULO_ORIGEM[e.origem] ?? e.origem}
                        {e.utmCampaign && (
                          <span className="block text-xs text-slate-400">{e.utmCampaign}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{formatarData(e.criadoEm)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Tabela>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                {n(total)} empresa{total === 1 ? '' : 's'} · página {pagina} de {n(paginas)}
              </span>
              <div className="flex gap-2">
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => p - 1)}
                >
                  Anterior
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={pagina >= paginas}
                  onClick={() => setPagina((p) => p + 1)}
                >
                  Próxima
                </Botao>
              </div>
            </div>
          </>
        ) : (
          <Vazio
            titulo="Nenhuma empresa encontrada"
            descricao="Ajuste os filtros ou importe um universo em Prospecção."
          />
        )}
      </Cartao>

      {/* Mantém a etiqueta visível para leitura rápida do funil. */}
      {resumo && (
        <Cartao titulo="Situação da carteira">
          <div className="flex flex-wrap gap-3">
            {ESTAGIOS.map((e) => (
              <Etiqueta key={e.valor} tom={e.tom}>
                {e.texto}: {n(resumo.dados.porEstagio[e.valor] ?? 0)}
              </Etiqueta>
            ))}
          </div>
        </Cartao>
      )}
    </div>
  )
}
